import { useEffect, useState } from "react";
import { CreditCard, Undo2, XCircle } from "lucide-react";
import { MonoLabel, SecondaryButton } from "../components/kit";
import { PricingPlans, PLAN_CARDS } from "../components/PricingPlans";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
const planName = (id) => PLAN_CARDS.find((p) => p.id === id)?.name || id;
const intervalLabel = (i) => (i === "quarter" ? "quarterly" : "monthly");

export default function BillingPage() {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [sub, setSub] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [confirm, setConfirm] = useState(null); // {plan, interval, kind}

  const load = async () => {
    const [s, su] = await Promise.allSettled([api.get("/billing/status"), api.get("/billing/subscription")]);
    if (s.status === "fulfilled") setStatus(s.value.data);
    if (su.status === "fulfilled") setSub(su.value.data);
    refreshUser();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changePlan = async (plan, interval) => {
    setBusyPlan(plan);
    try {
      const r = await api.post("/billing/change-plan", { plan, interval });
      if (r.data.applied === "immediate") {
        toast.success(`You're now on ${planName(plan)} (${intervalLabel(interval)}) — credits updated right away.`);
      } else if (plan === "starter") {
        toast.success(`Cancellation scheduled: you keep your current plan until ${fmtDate(r.data.effective_at)}, then Starter.`);
      } else {
        toast.success(`Change scheduled: from ${fmtDate(r.data.effective_at)} you'll switch to ${planName(plan)}.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyPlan(null);
    }
  };

  const select = async (plan, interval) => {
    if (sub?.has_subscription) {
      const rankUp = { starter: 0, founder: 1, pro: 2 }[plan] > ({ starter: 0, founder: 1, pro: 2 }[status?.plan] ?? 0);
      if (plan === "starter") return setConfirm({ plan, interval, kind: "cancel" });
      if (!rankUp && plan !== status?.plan) return setConfirm({ plan, interval, kind: "downgrade" });
      return changePlan(plan, interval);
    }
    setBusyPlan(plan);
    try {
      if (plan === "starter") {
        await api.post("/billing/select-plan", { plan: "starter", interval });
        toast.success("You're now on Starter.");
        await load();
      } else {
        const r = await api.post("/billing/checkout", { plan, interval, origin_url: window.location.origin });
        window.location.href = r.data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyPlan(null);
    }
  };

  const reactivate = async () => {
    try {
      await api.post("/billing/reactivate");
      toast.success("Subscription reactivated — no changes scheduled.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openPortal = async (flow) => {
    try {
      const r = await api.post("/billing/portal", flow ? { flow } : {});
      window.location.href = r.data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No active subscription to manage.");
    }
  };

  const pct = status ? Math.round((status.credits_left / Math.max(1, status.credits_total)) * 100) : 0;
  const pending = sub?.pending_change;
  const cancelling = pending?.plan === "starter" || sub?.cancel_at_period_end;

  return (
    <div className="p-5 sm:p-8 max-w-6xl" data-testid="billing-page">
      <MonoLabel>PLANS & CREDITS</MonoLabel>
      <h1 className="mt-3 text-2xl sm:text-3xl"><span className="font-serif-it">Your plan</span></h1>
      {status && (
        <div className="card-soft p-6 mt-6 max-w-2xl" data-testid="billing-status-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="font-mono-j text-[11px] tracking-[0.14em] text-[#54505B] uppercase">
                {status.plan || "no plan"} · {status.plan_status === "trial" ? `trial (${status.trial_days_left}d left)` : status.plan_status}
                {sub?.has_subscription && sub?.interval === "quarter" ? " · quarterly" : ""}
              </p>
              <p className="mt-2 text-[15px] font-bold text-[#181621]" data-testid="billing-credits-line">
                {status.credits_left} / {status.credits_total} credits · resets in {status.days_to_reset}d
              </p>
              {sub?.has_subscription && sub?.renewal_date && !cancelling && (
                <p className="mt-1 text-[12.5px] text-[#54505B]" data-testid="billing-renewal-line">
                  Next renewal: {fmtDate(sub.renewal_date)}
                </p>
              )}
            </div>
            {sub?.has_subscription && (
              <div className="flex items-center gap-2 flex-wrap">
                <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={() => openPortal("payment_method_update")} data-testid="billing-update-payment-btn">
                  <CreditCard size={14} strokeWidth={1.8} /> Update payment
                </SecondaryButton>
                {!cancelling && (
                  <SecondaryButton className="!py-2.5 !px-4 text-[13px] !text-[#B4443C]" onClick={() => setConfirm({ plan: "starter", interval: "month", kind: "cancel" })} data-testid="billing-cancel-btn">
                    <XCircle size={14} strokeWidth={1.8} /> Cancel plan
                  </SecondaryButton>
                )}
              </div>
            )}
          </div>
          {(pending || cancelling) && (
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap bg-[#FBF3E4] rounded-lg px-4 py-3" data-testid="billing-pending-notice">
              <p className="text-[13px] text-[#181621]">
                {cancelling
                  ? <>Your subscription ends on <b>{fmtDate(pending?.effective_at || sub?.renewal_date)}</b> — then you'll move to Starter (100 credits/month). Your account, Brand Brain and content stay yours.</>
                  : <>From <b>{fmtDate(pending.effective_at)}</b> you'll switch to <b>{planName(pending.plan)}</b> ({intervalLabel(pending.interval)}). Until then you keep your current plan and credits.</>}
              </p>
              <SecondaryButton className="!py-2 !px-3.5 text-[12.5px]" onClick={reactivate} data-testid="billing-reactivate-btn">
                <Undo2 size={13} strokeWidth={1.8} /> Undo change
              </SecondaryButton>
            </div>
          )}
          <div className="mt-4 h-2 rounded-full bg-[#EFEDE6] overflow-hidden">
            <div className="h-full rounded-full bg-[#181621] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-[12px] text-[#9A9591]">1 generated image = {status.image_cost} credits · plans, scripts and copy = 0 credits · credits don't roll over</p>
        </div>
      )}
      <div className="mt-10" id="change-plan">
        <MonoLabel>CHANGE PLAN</MonoLabel>
        <div className="mt-4">
          <PricingPlans
            onSelect={select}
            busyPlan={busyPlan}
            currentPlan={status?.plan_status === "active" || status?.plan === "starter" ? status?.plan : undefined}
            currentInterval={sub?.has_subscription ? sub?.interval : undefined}
            ctaLabel={(p, interval) => {
              if (p.id === "starter") return "Switch to Starter";
              if (status?.plan === p.id && status?.plan_status === "trial") return "Subscribe now";
              if (sub?.has_subscription && status?.plan === p.id) return interval === "quarter" ? "Switch to quarterly" : "Switch to monthly";
              return "Choose " + p.name;
            }}
          />
        </div>
        {sub?.has_subscription && (
          <p className="mt-4 text-[12px] text-[#9A9591]" data-testid="billing-change-rules-note">
            Upgrades and cycle changes are immediate (with Stripe proration). Downgrades start at the end of the cycle: no refunds, you keep your current credits until then.
          </p>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="billing-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "cancel" ? "Cancel your subscription?" : `Switch to ${planName(confirm?.plan)}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "cancel"
                ? "You stay on your current plan until the end of the paid period, then your account moves to Starter (100 credits/month). You won't lose your account, Brand Brain or generated content. You can reactivate anytime before it ends."
                : "The downgrade starts at the end of the cycle: no refund for the current period, and credits drop from the next cycle. You can undo the change before it takes effect."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-proceed"
              onClick={() => { const c = confirm; setConfirm(null); changePlan(c.plan, c.interval); }}>
              {confirm?.kind === "cancel" ? "Yes, cancel plan" : "Confirm downgrade"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
